import {
  Menu, X, XCircle, Search, Settings, SlidersHorizontal, LogOut, Bell, MessageSquare,
  HelpCircle, Info, AlertTriangle, Check, CircleCheck, Ban, Star, Send, Upload,
  UploadCloud, Download, ExternalLink, Link2, FilePlus, ImagePlus, SunMoon, Eye, EyeOff,
  Maximize2, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  ChevronsUp, ArrowUp, ArrowDown, Minus, User, Users, CircleUser, UserCog, Handshake,
  Contact, Bot, Server, Cpu, Activity, Plug, Zap, ChartNoAxesCombined, Percent, Coins,
  Receipt, Layers, PackageSearch, ScanLine, ArrowLeftRight, Scale, Boxes, Target,
  Package, Truck, Car,
  TrendingUp, Tag, Wallet, Gem, List, Database, Hash, FileText, Terminal, BookOpen,
  FileSearch, Fingerprint, Wrench,
} from 'lucide-react';
import React, { useState } from 'react';
import { PlacesType, Tooltip } from 'react-tooltip';
import { randomString } from '/imports/utils';

export interface IconProps extends React.HTMLAttributes<HTMLSpanElement> {
  icon: IconKey,
  stroke?: string,
  strokeWidth?: string,
  fill?: string,
  size?: number,
  styleTag?: string,
  onClick?: React.MouseEventHandler,
  'data-tooltip-id'?: string,
  tip?: string | React.ReactNode,
  tipEvents?: {
    mouseenter?: boolean
    focus?: boolean
    mouseover?: boolean
    click?: boolean
    dblclick?: boolean
    mousedown?: boolean
  };
  tipPlace?: PlacesType | undefined;
  className?: string,
}

// `icon`, `stroke`, `fill`, `size`, `tip*` and `styleTag` are component props,
// not DOM attributes: spreading them onto the span leaks them into the HTML and
// React 19 warns about the unknown ones. Held as a key list rather than a
// rest-destructure with `_`-prefixed bindings, which reads as dead code and
// which the lint config rejects outright.
const COMPONENT_PROP_KEYS: readonly string[] = [
  'icon', 'stroke', 'strokeWidth', 'fill', 'size',
  'styleTag', 'tip', 'tipEvents', 'tipPlace', 'className', 'onClick',
];

const domPropsOf = (props: IconProps): React.HTMLAttributes<HTMLSpanElement> => (
  Object.fromEntries(Object.entries(props).filter(([key]) => !COMPONENT_PROP_KEYS.includes(key)))
);

export const Icon = (props: IconProps) => {
  const [tipId,] = useState(props['data-tooltip-id'] || `tip${randomString()}`);
  const domProps = domPropsOf(props);
  const Glyph = iconGlyph[props.icon] ?? HelpCircle;
  /*
   * lucide draws with `stroke`, where the old Material set drew with `fill`.
   * Both default to currentColor here, so an icon still inherits the
   * surrounding text colour and stays theme-aware. `fill` is only forwarded
   * when a caller asks for it: passing currentColor by default would flood
   * every outline icon into a solid blob.
   */
  const size = Number(props.size || 24);
  return (
    <>
      <span
        {...domProps}
        data-tooltip-id={tipId}
        className={`icon inline-flex flex-col justify-around [&_svg]:min-w-4 ${props.className || ''}`}
        onClick={props.onClick}
      >
        <Glyph
          size={size}
          color={props.stroke || 'currentColor'}
          strokeWidth={Number(props.strokeWidth) || 1.75}
          {...(props.fill ? { fill: props.fill } : {})}
        />
      </span>
      {
        props.tip ?
          <Tooltip id={tipId} content={props.tip as string} place={props.tipPlace} openEvents={props.tipEvents || { mouseover: true }} />
          :
          null
      }
    </>
  );
};

/**
 * Every icon key in the app, mapped to a lucide glyph.
 *
 * The keys are unchanged from the old Material set so no call site had to move;
 * only the artwork behind them differs. Where the old name described a Material
 * glyph rather than a meaning (`batteryShare`, `coPresent`, `switchAccess`) the
 * lucide choice follows what the icon is USED for in this app, which is why a
 * few look unrelated to their key.
 */
export const iconGlyph = {
  // --- chrome -------------------------------------------------------------
  menu: Menu,
  close: X,
  cancel: XCircle,
  search: Search,
  settings: Settings,
  tune: SlidersHorizontal,
  logout: LogOut,
  notifications: Bell,
  chat: MessageSquare,
  help: HelpCircle,
  info: Info,
  warning: AlertTriangle,
  check: Check,
  circleCheck: CircleCheck,
  block: Ban,
  star: Star,
  send: Send,
  upload: Upload,
  publish: UploadCloud,
  download: Download,
  openInNew: ExternalLink,
  link2: Link2,
  postAdd: FilePlus,
  addPhotoAlternate: ImagePlus,
  modeOffOn: SunMoon,
  visibility: Eye,
  visibilityOff: EyeOff,
  expand: Maximize2,
  expandMore: ChevronDown,
  keyboardArrowDown: ChevronDown,
  chevronLeft: ChevronLeft,
  chevronRight: ChevronRight,
  keyboardDoubleArrowLeft: ChevronsLeft,
  keyboardDoubleArrowRight: ChevronsRight,
  keyboardDoubleArrowUp: ChevronsUp,
  arrowUp: ArrowUp,
  arrowDown: ArrowDown,
  remove: Minus,

  // --- people -------------------------------------------------------------
  person: User,
  group: Users,
  accountCircle: CircleUser,
  manageAccounts: UserCog,
  coPresent: Handshake,
  idCard: Contact,

  // --- domain -------------------------------------------------------------
  construction: Bot,
  dns: Server,
  developerBoard: Cpu,
  electricMeter: Activity,
  electricalServices: Plug,
  solarPower: Zap,
  queryStats: ChartNoAxesCombined,
  percent: Percent,
  attachMoney: Coins,
  requestPage: Receipt,
  docs: Layers,
  documentSearch: PackageSearch,
  documentScanner: ScanLine,
  switchAccess: ArrowLeftRight,
  batteryShare: Scale,
  hub: Boxes,
  speed: Target,
  localShipping: Package,
  deliveryTruckSpeed: Truck,
  directionsCar: Car,

  // --- semantic keys ------------------------------------------------------
  // Named for MEANING rather than for a Material glyph. The nav uses these, so
  // a label and its icon can be checked against each other by reading one line.
  trendingUp: TrendingUp,
  robot: Bot,
  target: Target,
  tag: Tag,
  wallet: Wallet,
  receipt: Receipt,
  packageBox: Package,
  boxes: Boxes,
  layers: Layers,
  coins: Coins,
  handshake: Handshake,
  pulse: Activity,
  gem: Gem,
  list: List,
  database: Database,
  plug: Plug,
  hash: Hash,
  fileText: FileText,
  terminal: Terminal,
  bookOpen: BookOpen,
  searchDoc: FileSearch,
  fingerprint: Fingerprint,
  wrench: Wrench,
} as const;

export type IconKey = keyof typeof iconGlyph